import { useRef, useEffect, useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleStop, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getConversation, sendMessageStream, confirmAction, cancelAction, cancelGeneration, regenerate, deleteMessage } from '../../api/assistant';
import type { SSESource } from '../../api/sse';
import { ChatMessage, MarkdownContent } from './ChatMessage';
import { MessageComposer } from './MessageComposer';
import { ActionConfirmationCard } from '../confirmation/ActionConfirmationCard';
import { UiGuidanceLayer } from '../guidance/UiGuidanceLayer';
import { ThinkingPanel, type ThinkingStep } from './ThinkingPanel';
import { collectPageContext } from '../../utils/pageContext';
import type {
  Message,
  ActionConfirmationEvent,
  ThinkingEvent,
  ToolCallEvent,
  UsageEvent,
  UsageWarningEvent,
  LimitExceededEvent,
  UiGuidanceEvent,
} from '../../types';

/** Auto-scroll only snaps to bottom when already within this many px of it. */
const NEAR_BOTTOM_THRESHOLD_PX = 80;

interface ChatViewProps {
  conversationId: string;
}

export function ChatView({ conversationId }: ChatViewProps) {
  const queryClient = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [pendingAction, setPendingAction] = useState<ActionConfirmationEvent | null>(null);
  const [actionStatus, setActionStatus] = useState<'pending' | 'confirming' | 'confirmed' | 'cancelled' | 'error'>('pending');
  const [actionError, setActionError] = useState<string>();
  const [streamingContent, setStreamingContent] = useState<string>('');
  const [streamError, setStreamError] = useState<string | null>(null);
  // Non-blocking: shown while a turn approaching the daily spend cap still completes normally.
  const [usageWarning, setUsageWarning] = useState<string | null>(null);
  // Blocking: the turn was never started because the caller is already over the daily spend cap.
  const [limitExceeded, setLimitExceeded] = useState<string | null>(null);
  // Live running cost for the in-flight turn, updated after each LLM round trip — null while
  // nothing's in flight, or once every round trip so far has been on an unpriced model.
  const [turnCostUsd, setTurnCostUsd] = useState<number | null>(null);
  const [thinkingSteps, setThinkingSteps] = useState<ThinkingStep[]>([]);
  const [thinkingActive, setThinkingActive] = useState(false);
  const [uiGuidance, setUiGuidance] = useState<UiGuidanceEvent | null>(null);
  const [, setThinkingStartedAt] = useState<number | null>(null);
  const [thinkingElapsedSeconds, setThinkingElapsedSeconds] = useState<number | undefined>(undefined);
  // Shown immediately on send, before the (possibly slow) assistant response comes back —
  // otherwise the user's own message doesn't appear until the whole round trip finishes,
  // which reads as the UI being stuck/buggy rather than just waiting on the assistant.
  const [pendingUserContent, setPendingUserContent] = useState<string | null>(null);
  // True while a regenerate/retry is in flight — the old response is hidden immediately (rather
  // than staying on screen until the new one finishes streaming) since the backend has already
  // deleted it server-side; leaving it visible until the swap made regenerate look like it was
  // appending a second answer instead of replacing the first.
  const [isRegenerating, setIsRegenerating] = useState(false);
  // The currently open SSE connection, if any — kept so the Stop button can close it directly
  // for an instant UI stop, independent of how long the server-side call takes to notice.
  const activeSourceRef = useRef<SSESource | null>(null);
  // The content of the last message the user sent, for the Retry/Regenerate actions.
  const lastSentContentRef = useRef<string | null>(null);
  const voiceReplyPendingRef = useRef(false);
  const clearUiGuidance = useCallback(() => setUiGuidance(null), []);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['assistant-conversation', conversationId],
    queryFn: () => getConversation(conversationId),
  });

  const [isSending, setIsSending] = useState(false);

  const allMessages: Message[] = data?.messages ?? [];
  // While regenerating, hide everything after the last USER message immediately — the backend
  // has already deleted those rows (regenerate discards the old response server-side before
  // streaming a fresh one), so keeping them visible until the new stream finishes just makes it
  // look like a second answer is being appended rather than the first being replaced.
  let lastUserIndex = -1;
  for (let i = allMessages.length - 1; i >= 0; i--) {
    if (allMessages[i].role === 'USER') {
      lastUserIndex = i;
      break;
    }
  }
  const messages: Message[] = isRegenerating ? allMessages.slice(0, lastUserIndex + 1) : allMessages;

  // Auto-scroll to bottom — but only when the user is already near it. If they've scrolled up
  // to reread something mid-stream, new chunks arriving shouldn't yank them back down.
  const isNearBottomRef = useRef(true);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  const stopAssistantSpeech = useCallback(() => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;

    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  }, []);

  const speakAssistantResponse = useCallback((content: string) => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window) || !content.trim()) return;

    stopAssistantSpeech();
    const utterance = new SpeechSynthesisUtterance(content);
    utterance.lang = 'en-US';
    utterance.rate = 1;
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    setIsSpeaking(true);
    window.speechSynthesis.speak(utterance);
  }, [stopAssistantSpeech]);

  useEffect(() => {
    return () => {
      stopAssistantSpeech();
    };
  }, [stopAssistantSpeech]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isNearBottomRef.current = distanceFromBottom < NEAR_BOTTOM_THRESHOLD_PX;
  }, []);

  useEffect(() => {
    if (isNearBottomRef.current) {
      scrollToBottom();
    }
  }, [messages.length, pendingAction, streamingContent, scrollToBottom]);

  // Switching conversations reuses this component instance (no remount) — without this, an
  // in-flight or just-stopped turn's ephemeral bubbles (pendingUserContent, streamingContent,
  // thinkingSteps) would leak into the newly-selected conversation's view.
  useEffect(() => {
    activeSourceRef.current?.close();
    activeSourceRef.current = null;
    setStreamingContent('');
    setStreamError(null);
    setUsageWarning(null);
    setLimitExceeded(null);
    setTurnCostUsd(null);
    setThinkingSteps([]);
    setThinkingActive(false);
    setThinkingStartedAt(null);
    setThinkingElapsedSeconds(undefined);
    setPendingUserContent(null);
    setIsSending(false);
    setIsRegenerating(false);
    voiceReplyPendingRef.current = false;
    stopAssistantSpeech();
    clearUiGuidance();
  }, [conversationId, clearUiGuidance, stopAssistantSpeech]);

  const finishThinking = useCallback(() => {
    setThinkingActive(false);
    setThinkingStartedAt((startedAt) => {
      if (startedAt !== null) {
        setThinkingElapsedSeconds(Math.round((Date.now() - startedAt) / 1000));
      }
      return startedAt;
    });
  }, []);

  /** Process an SSE stream from either the initial send or confirm-action — both use the same event contract. */
  const processSSEStream = useCallback((source: SSESource, onSettled: () => void, onCompletedContent?: (content: string) => void) => {
    activeSourceRef.current = source;
    let completedContent = '';
    setStreamingContent('');
    setStreamError(null);
    setLimitExceeded(null);
    setTurnCostUsd(null);
    setThinkingSteps([]);
    setThinkingActive(false);
    setThinkingStartedAt(null);
    setThinkingElapsedSeconds(undefined);

    source.addEventListener('thinking', (e) => {
      const event: ThinkingEvent = JSON.parse(e.data);
      setThinkingActive(true);
      setThinkingStartedAt((prev) => prev ?? Date.now());
      // Reasoning now arrives token-by-token — append onto the in-progress step for this
      // iteration rather than adding a new line per fragment.
      setThinkingSteps((prev) => {
        const last = prev[prev.length - 1];
        if (last && last.type === 'thinking' && last.iteration === event.iteration) {
          return [...prev.slice(0, -1), { ...last, text: last.text + event.text }];
        }
        return [...prev, { type: 'thinking', text: event.text, iteration: event.iteration }];
      });
    });

    // Surfaced as a step in the same panel — without this the panel goes silent while a tool
    // runs (tool_call previously fired but had no listener at all, so it just vanished).
    source.addEventListener('tool_call', (e) => {
      const event: ToolCallEvent = JSON.parse(e.data);
      setThinkingActive(true);
      setThinkingSteps((prev) => [...prev, { type: 'tool_call', text: `Calling tool: ${event.toolName}` }]);
    });

    source.addEventListener('message', (e) => {
      finishThinking();
      completedContent += e.data;
      setStreamingContent((prev) => prev + e.data);
    });

    source.addEventListener('action_confirmation', (e) => {
      finishThinking();
      const event: ActionConfirmationEvent = JSON.parse(e.data);
      setPendingAction(event);
      setActionStatus('pending');
      setActionError(undefined);
    });

    source.addEventListener('ui_guidance', (e) => {
      const event: UiGuidanceEvent = JSON.parse(e.data);
      if (event.action === 'clear_guidance') {
        clearUiGuidance();
        return;
      }
      setUiGuidance(event);
    });

    // One of these arrives after every LLM round trip in the turn (a turn can be several, if
    // tools are called) — this is the running total, not a delta, so just replacing state is
    // correct even across multiple tool-calling iterations.
    source.addEventListener('usage', (e) => {
      const event: UsageEvent = JSON.parse(e.data);
      if (event.priced) {
        setTurnCostUsd(event.turnCostUsd);
      }
    });

    // Non-blocking — the turn still completes normally, this just surfaces how close the caller
    // is to today's spend cap so it isn't a surprise when a later turn gets blocked outright.
    source.addEventListener('usage_warning', (e) => {
      const event: UsageWarningEvent = JSON.parse(e.data);
      setUsageWarning(
        `Approaching today's AI assistant usage limit: $${event.spentTodayUsd.toFixed(2)} of ` +
        `$${event.dailyCapUsd.toFixed(2)} used.`
      );
    });

    // Blocking — the backend never started this turn (no Bedrock call, nothing persisted), so
    // there's nothing to refetch and no partial content to leave on screen.
    source.addEventListener('limit_exceeded', (e) => {
      source.close();
      activeSourceRef.current = null;
      setStreamingContent('');
      setThinkingSteps([]);
      finishThinking();
      const event: LimitExceededEvent = JSON.parse(e.data);
      setLimitExceeded(event.message);
      onSettled();
    });

    source.addEventListener('done', () => {
      source.close();
      activeSourceRef.current = null;
      setThinkingActive(false);
      setTurnCostUsd(null);
      onCompletedContent?.(completedContent);
      // Wait for the persisted message to actually land in the query cache before clearing the
      // live-streamed bubble — clearing immediately (the old behavior) leaves a gap between "the
      // streamed text disappears" and "the refetch resolves and the real message renders," which
      // is the flash/flicker on every completed turn. refetchQueries (not invalidateQueries)
      // returns a promise we can actually wait on.
      queryClient.refetchQueries({ queryKey: ['assistant-conversation', conversationId] })
        .catch(() => {
          // If the refetch itself fails, still clear the ephemeral bubbles below rather than
          // leaving a stale streamed answer on screen forever.
        })
        .finally(() => {
          setStreamingContent('');
          // The persisted message renders its own thinking panel from `reasoningContent` —
          // without clearing this, the live in-flight panel lingers alongside it as a visually
          // duplicate "second" thinking panel.
          setThinkingSteps([]);
          onSettled();
        });
      queryClient.invalidateQueries({ queryKey: ['assistant-usage'] });
    });

    source.addEventListener('error', (e) => {
      source.close();
      activeSourceRef.current = null;
      setStreamingContent('');
      setThinkingSteps([]);
      setTurnCostUsd(null);
      finishThinking();
      const errorMessage = e.data || 'An error occurred while processing your request.';
      setStreamError(errorMessage);
      queryClient.invalidateQueries({
        queryKey: ['assistant-conversation', conversationId],
      });
      onSettled();
    });
  }, [conversationId, queryClient, finishThinking, clearUiGuidance]);

  /**
   * Stops listening immediately (client-side) and best-effort tells the backend to stop too.
   * Deliberately leaves the user's question and the assistant's partial answer on screen exactly
   * where generation stopped, rather than clearing them — the backend persists that same partial
   * turn once its one in-flight (uninterruptible) LLM call returns (see
   * AssistantCancellationService), so the next natural refetch of this conversation picks up the
   * real messages and seamlessly replaces these ephemeral bubbles with no visible change.
   */
  const handleStop = useCallback(() => {
    activeSourceRef.current?.close();
    activeSourceRef.current = null;
    finishThinking();
    setIsSending(false);
    stopAssistantSpeech();
    cancelGeneration(conversationId).catch(() => {
      // Best-effort — the backend may not even have a turn in flight to cancel by the time this
      // lands, and the client has already stopped listening regardless.
    });
  }, [conversationId, finishThinking, stopAssistantSpeech]);

  const handleConfirm = useCallback(() => {
    if (!pendingAction) return;
    setActionStatus('confirming');
    const source = confirmAction(conversationId);
    setActionStatus('confirmed');
    setPendingAction(null);
    processSSEStream(source, () => {});
  }, [pendingAction, conversationId, processSSEStream]);

  const handleCancel = useCallback(async () => {
    if (!pendingAction) return;
    try {
      await cancelAction(conversationId);
      setActionStatus('cancelled');
      setPendingAction(null);
      // Refetch to pick up any server-side state changes
      queryClient.invalidateQueries({
        queryKey: ['assistant-conversation', conversationId],
      });
    } catch {
      setActionStatus('error');
      setActionError('Failed to cancel action');
    }
  }, [pendingAction, conversationId, queryClient]);

  const handleSend = useCallback((content: string, options?: { voiceReply?: boolean }) => {
    isNearBottomRef.current = true; // sending is an explicit action — re-anchor to bottom
    stopAssistantSpeech();
    voiceReplyPendingRef.current = Boolean(options?.voiceReply);
    lastSentContentRef.current = content;
    setStreamError(null);
    setPendingUserContent(content); // Show the user's message right away
    setIsSending(true);
    clearUiGuidance();
    const source = sendMessageStream(conversationId, { content, pageContext: collectPageContext() });
    processSSEStream(source, () => {
      setIsSending(false);
      setPendingUserContent(null);
    }, (completedContent) => {
      if (voiceReplyPendingRef.current) {
        speakAssistantResponse(completedContent);
      }
      voiceReplyPendingRef.current = false;
    });
  }, [conversationId, processSSEStream, clearUiGuidance, speakAssistantResponse, stopAssistantSpeech]);

  /** Discards the previous response to the last user message and streams a fresh one in its
   *  place — used by both the error Retry action and the Regenerate action under the most recent
   *  assistant reply. A true in-place regenerate: the old response (or error) is deleted
   *  server-side rather than this appending a whole new turn on top of it. */
  /** Deletes a previously-asked question and its response (one turn) and refetches. */
  const handleDeleteMessage = useCallback((messageId: string) => {
    deleteMessage(conversationId, messageId)
      .then(() => queryClient.refetchQueries({ queryKey: ['assistant-conversation', conversationId] }))
      .catch(() => {
        // Best-effort — a stale/already-refetched view will self-correct on the next natural
        // refetch; no need for a dedicated error banner over a delete click.
      });
  }, [conversationId, queryClient]);

  const retryLastMessage = useCallback(() => {
    if (isSending) return;
    isNearBottomRef.current = true;
    setStreamError(null);
    setIsSending(true);
    setIsRegenerating(true);
    const source = regenerate(conversationId);
    processSSEStream(source, () => {
      setIsSending(false);
      setIsRegenerating(false);
    });
  }, [conversationId, processSSEStream, isSending]);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <UiGuidanceLayer guidance={uiGuidance} onClear={clearUiGuidance} />
      <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.length === 0 && !pendingAction && !pendingUserContent && (
          <p className="text-sm text-muted-foreground text-center mt-8">
            Send a message to start the conversation
          </p>
        )}
        {messages.map((msg, i) => {
          const isLastAssistantMessage = msg.role === 'ASSISTANT' && i === messages.length - 1;
          return (
            <div key={msg.id}>
              <ChatMessage
                message={msg}
                onDelete={
                  msg.role === 'USER' && !isSending && !pendingAction
                    ? () => handleDeleteMessage(msg.id)
                    : undefined
                }
              />
              {/* Regenerate — only on the most recent assistant reply, and not mid-send. An
                  assistant message always follows a user message, so no extra guard is needed
                  to know there's something to regenerate (unlike the error Retry action below,
                  which only applies to a failure from a send/regenerate earlier this session). */}
              {isLastAssistantMessage && !isSending && !pendingAction && (
                <button
                  onClick={retryLastMessage}
                  data-testid="regenerate-button"
                  className="ml-9 mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <RotateCcw className="h-3 w-3" />
                  Regenerate
                </button>
              )}
            </div>
          );
        })}

        {/* Optimistic user message — shown immediately on send, before the assistant
            has replied, so the UI doesn't look stuck while waiting on a slow response. */}
        {pendingUserContent && (
          <ChatMessage
            message={{
              id: 'pending-user-message',
              conversationId,
              role: 'USER',
              content: pendingUserContent,
              createdAt: new Date().toISOString(),
            }}
          />
        )}

        {/* Thinking / tool-call steps for the in-flight turn */}
        {thinkingSteps.length > 0 && (
          <div className="flex justify-start">
            <ThinkingPanel steps={thinkingSteps} active={thinkingActive} elapsedSeconds={thinkingElapsedSeconds} />
          </div>
        )}

        {/* Streaming assistant content */}
        {streamingContent && (
          <div className="flex justify-start gap-2">
            <div className="bg-muted rounded-lg px-3 py-2 text-sm max-w-[80%]">
              <MarkdownContent content={streamingContent} />
            </div>
          </div>
        )}

        {/* Streaming error */}
        {streamError && (
          <div className="flex justify-start gap-2">
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2 text-sm text-destructive max-w-[80%] space-y-1.5">
              <p className="whitespace-pre-wrap break-words">{streamError}</p>
              {lastSentContentRef.current && (
                <button
                  onClick={retryLastMessage}
                  data-testid="retry-button"
                  className="inline-flex items-center gap-1 text-xs font-medium text-destructive hover:underline"
                >
                  <RotateCcw className="h-3 w-3" />
                  Retry
                </button>
              )}
            </div>
          </div>
        )}

        {/* Pending action confirmation card */}
        {pendingAction && (
          <div className="flex justify-start gap-2">
            <div className="max-w-[90%]">
              <ActionConfirmationCard
                toolName={pendingAction.toolName}
                description={pendingAction.description}
                previewType={pendingAction.previewType}
                previewData={pendingAction.previewData}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
                status={actionStatus}
                errorMessage={actionError}
              />
            </div>
          </div>
        )}

        {/* Action cancelled inline notice */}
        {!pendingAction && actionStatus === 'cancelled' && (
          <div className="flex justify-start gap-2">
            <div className="bg-muted rounded-lg px-3 py-2 text-sm text-muted-foreground">
              Action cancelled
            </div>
          </div>
        )}

        {isSending && !streamingContent && thinkingSteps.length === 0 && (
          <div className="flex justify-start">
            <div className="bg-muted rounded-lg px-3 py-2">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          </div>
        )}
      </div>
      {isSending && turnCostUsd !== null && (
        <div className="mx-3 mb-1 text-right text-[11px] text-muted-foreground">
          ~${turnCostUsd < 0.01 ? turnCostUsd.toFixed(4) : turnCostUsd.toFixed(2)} this turn so far
        </div>
      )}
      {usageWarning && (
        <div className="mx-3 mb-2 flex items-start justify-between gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
          <span>{usageWarning}</span>
          <button
            onClick={() => setUsageWarning(null)}
            aria-label="Dismiss usage warning"
            className="shrink-0 text-amber-700/70 hover:text-amber-700 dark:text-amber-400/70 dark:hover:text-amber-400"
          >
            &times;
          </button>
        </div>
      )}
      {limitExceeded && (
        <div className="mx-3 mb-2 rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {limitExceeded}
        </div>
      )}
      {isSpeaking && (
        <div className="mx-3 mb-2 flex justify-end">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={stopAssistantSpeech}
            className="h-8 gap-1.5 text-xs"
            aria-label="Stop speaking"
            data-agent-target="assistant-stop-speaking-button"
          >
            <CircleStop className="h-3.5 w-3.5" />
            Stop speaking
          </Button>
        </div>
      )}
      <MessageComposer
        onSend={handleSend}
        onUserTurnStart={stopAssistantSpeech}
        disabled={actionStatus === 'confirming'}
        isGenerating={isSending}
        onStop={handleStop}
      />
    </>
  );
}
